import React, { useState, useCallback } from "react";
import { useTokenContext } from "../contexts/TokenContext";
import { useChatContext } from "../contexts/ChatContext";
import { useSheetContext } from "../contexts/SheetContext";
import { useSessionContext } from "../contexts/SessionContext";
import { Button } from "./ui/button";
import SidebarPanel from "./ui/SidebarPanel";
import { Label } from "./ui/label";
import { IconButton } from "./ui/icon-button";
import { ListRow } from "./ui/row";
import { CircleUser, Plus, EyeOff, Eye, FileText, Trash2, Link2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import InputDialog from "./InputDialog";
import { useI18n } from "../i18n";
import { useUndoContext } from "../contexts/UndoContext";
import { createTokenCreateCommand, createTokenDeleteCommand } from "../lib/undo-commands";
import { serializeToken } from "../lib/token-utils";
import type { TokenSnapshot } from "../types";

const PRESETS: { color: string }[] = [
  { color: "#e74c3c" },
  { color: "#3498db" },
  { color: "#2ecc71" },
  { color: "#f39c12" },
  { color: "#9b59b6" },
  { color: "#1abc9c" },
];

const TokenPanel = React.memo(function TokenPanel({
  onAddToken,
  onOpenSheet,
}: { onAddToken?: () => void; onOpenSheet?: (sheetId: string) => void } = {}) {
  const { tokens } = useTokenContext();
  const { t } = useI18n();
  const { chat } = useChatContext();
  const { sheets: charSheets, onSendSheet } = useSheetContext();
  const { outgoingPageId, mp, isGM, sessionSettings } = useSessionContext();
  const sheets = charSheets.sheets;
  const undoStack = useUndoContext();

  // Same read-gate as the canvas double-click path (TokenManager
  // handleDoubleClickToken) — GM, owner, or viewOtherSheets === 'full'.
  // Without it this panel opened the HUD for any bound token.
  const canReadSheet = useCallback(
    (sheetId: string): boolean => {
      if (isGM) return true;
      const sheet = sheets.find((s) => s.id === sheetId);
      if (!sheet) return false;
      if (sheet.ownerName === mp.participant?.name) return true;
      return (sessionSettings.playerPermissions?.viewOtherSheets ?? 'basic') === 'full';
    },
    [isGM, sheets, mp.participant?.name, sessionSettings],
  );

  const onBindSheet = useCallback(
    (sheetId: string, tokenId: string) => {
      onSendSheet({
        type: "sheet:bind-token",
        sheetId,
        tokenId,
        pageId: outgoingPageId,
      });
    },
    [onSendSheet, outgoingPageId],
  );

  const onUnbindSheet = useCallback(
    (sheetId: string) => {
      onSendSheet({ type: "sheet:unbind-token", sheetId });
    },
    [onSendSheet],
  );
  const [presetDialogOpen, setPresetDialogOpen] = useState(false);
  const [presetColor, setPresetColor] = useState("");

  const handlePreset = (color: string) => {
    setPresetColor(color);
    setPresetDialogOpen(true);
  };

  // A preset token is a real token: the server gets the create (as the file upload and the canvas dialog do), else it
  // lives on this screen only — gone after a reload, and every later move / edit answers «not found».
  const handlePresetConfirm = useCallback(
    (name: string) => {
      const tokenName = name || t('tokens.defaultName');
      const tok = tokens.addToken({ color: presetColor, name: tokenName });
      chat.addSystemMessage(`${t('tokens.added')} ${tokenName}`);
      const snapshot: TokenSnapshot = serializeToken(tok);
      if (mp.session) mp.send({ type: "token:create", token: snapshot, pageId: outgoingPageId } as any);
      undoStack.push(
        createTokenCreateCommand({
          token: snapshot,
          addToken: (x) => tokens.addTokenDirect(x),
          removeToken: (id) => tokens.removeToken(id),
          sendCreate: (x) => { if (mp.session) mp.send({ type: "token:create", token: x, pageId: outgoingPageId } as any); },
          sendDelete: (id) => { if (mp.session) mp.send({ type: "token:delete", tokenId: id, pageId: outgoingPageId } as any); },
        }),
      );
    },
    [presetColor, tokens, chat, t, mp, outgoingPageId, undoStack],
  );

  // Sidebar polish П3/П5: the row's eye hides / shows the token (the canvas menu's «toggle-visible»), «⋯» deletes after asking.
  const setVisible = useCallback(
    (tokenId: string, visible: boolean) => {
      tokens.updateToken(tokenId, { visible });
      if (mp.session) mp.send({ type: "token:update", tokenId, changes: { visible }, pageId: outgoingPageId } as any);
    },
    [tokens, mp, outgoingPageId],
  );
  const deleteToken = useCallback(
    (tokenId: string) => {
      const tok = tokens.tokens.find((x) => x.id === tokenId);
      if (!tok || !mp.session) return;
      const { imageEl: _img, ...snapshot } = tok as typeof tok & { imageEl?: unknown };
      mp.send({ type: "token:delete", tokenId, pageId: outgoingPageId } as any);
      undoStack.push(
        createTokenDeleteCommand({
          token: snapshot as any,
          addToken: (x) => tokens.addTokenDirect(x),
          removeToken: (id) => tokens.removeToken(id),
          sendCreate: (x) => { if (mp.session) mp.send({ type: "token:create", token: x, pageId: outgoingPageId } as any); },
          sendDelete: (id) => { if (mp.session) mp.send({ type: "token:delete", tokenId: id, pageId: outgoingPageId } as any); },
        }),
      );
    },
    [tokens, mp, outgoingPageId, undoStack],
  );

  const selectedToken = tokens.selectedToken;
  const boundSheetId = selectedToken?.characterSheetId;

  return (
    <SidebarPanel
      id="tokens"
      title={t('tokens.title')}
      icon={CircleUser}
      count={tokens.tokens.length}
      tight
      actions={onAddToken ? (
        <IconButton label={t('sb.map.addToken')} icon={<Plus size={14} aria-hidden="true" />} onClick={onAddToken} data-testid="toolbar-btn-add-token" />
      ) : undefined}
    >
          {tokens.tokens.length === 0 ? <p className="sb-line py-1 text-meta text-dim">{t('sb.map.noTokens')}</p> : null}
          <ul id="token-list" className="flex flex-col">
            {tokens.tokens.map((tok) => {
              const readable = !!(onOpenSheet && tok.characterSheetId && canReadSheet(tok.characterSheetId));
              const mine = !isGM && !!tok.characterSheetId && sheets.find((s) => s.id === tok.characterSheetId)?.ownerName === mp.participant?.name;
              return (
                <ListRow
                  key={tok.id}
                  className={`token-list-item ${tokens.selectedTokenIds.has(tok.id) ? "selected" : ""}`}
                  current={tokens.selectedTokenIds.has(tok.id)}
                  label={tok.name}
                  dot={tok.color}
                  onOpen={() => tokens.selectToken(tok.id)}
                  onOpenDouble={() => {
                    if (readable) onOpenSheet!(tok.characterSheetId!);
                  }}
                  // maxHp 0: the server sent no hit points (a player does not get a monster's) — nothing to draw
                  meta={tok.maxHp > 0 ? `${tok.hp}/${tok.maxHp}` : mine ? t('sb.tok.mine') : undefined}
                  deco={[
                    ...(tok.characterSheetId ? [{ icon: <Link2 size={12} aria-hidden="true" />, label: t('tokens.boundToSheet') }] : []),
                    ...(isGM && tok.visible === false ? [{ icon: <EyeOff size={12} aria-hidden="true" />, label: t('sb.gmOnly') }] : []),
                  ]}
                  actions={isGM ? (
                    <IconButton
                      size="sm"
                      label={tok.visible === false ? t('sb.tok.show') : t('sb.tok.hide')}
                      icon={tok.visible === false ? <Eye size={14} aria-hidden="true" /> : <EyeOff size={14} aria-hidden="true" />}
                      onClick={() => setVisible(tok.id, tok.visible === false)}
                    />
                  ) : undefined}
                  menu={isGM || readable ? [
                    { label: t('sb.sheet.open'), icon: FileText, onSelect: () => onOpenSheet?.(tok.characterSheetId!), hidden: !readable },
                    { separator: true, hidden: !isGM },
                    {
                      label: t('sb.delete'),
                      icon: Trash2,
                      danger: true,
                      hidden: !isGM,
                      confirm: { title: t('sb.tok.deleteTitle').replace('{name}', tok.name), body: t('sb.tok.deleteBody'), yes: t('sb.deleteYes') },
                      onSelect: () => deleteToken(tok.id),
                    },
                  ] : undefined}
                />
              );
            })}
          </ul>
          <div className="token-presets">
            {PRESETS.map((p) => (
              <Button
                key={p.color}
                variant="ghost"
                size="swatch"
                className="preset-token"
                onClick={() => handlePreset(p.color)}
                title={`${t('tokens.presetToken')} ${p.color}`}
                data-testid={`token-preset-${p.color}`}
              >
                <span
                  className="inline-block w-4 h-4 rounded-full"
                  style={{ background: p.color }}
                />
              </Button>
            ))}
          </div>
          {isGM && selectedToken && sheets.length > 0 && (
            <div
              className="token-binding"
              style={{ marginTop: 6, fontSize: 12 }}
              onClick={(e) => e.stopPropagation()}
            >
              <Label style={{ color: "var(--text-muted)" }}>{t('tokens.sheetLabel')}</Label>
              <Select
                value={boundSheetId ?? "__none__"}
                onValueChange={(val) => {
                  if (val === "__none__") {
                    if (boundSheetId) {
                      const sheet = sheets.find((s) => s.id === boundSheetId);
                      if (sheet) onUnbindSheet(sheet.id);
                    }
                  } else {
                    onBindSheet(val, selectedToken.id);
                  }
                }}
              >
                <SelectTrigger
                  className="mt-0.5 h-7 w-full text-meta"
                  onClick={(e) => e.stopPropagation()}
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">{t('common.none')}</SelectItem>
                  {sheets.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name} ({s.sheetType.toUpperCase()})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
      <InputDialog
        open={presetDialogOpen}
        onOpenChange={setPresetDialogOpen}
        title={t('tokens.tokenName')}
        defaultValue={t('tokens.defaultName')}
        onConfirm={handlePresetConfirm}
      />
    </SidebarPanel>
  );
});

export default TokenPanel;
